import axios from 'axios';

/** Exact cancel/abort messages used by axios CancelToken / AbortController (no HTTP response). */
const CANCEL_MESSAGE_RE = /^(canceled|cancelled|abort(ed)?|request aborted)$/i;

/**
 * True for axios cancel / abort — these are intentional and must not flood GlitchTip.
 * Supports axios@0.19 `isCancel`, `__CANCEL__`, `ERR_CANCELED`, AbortError, and bare cancel messages.
 * Timeouts (`ECONNABORTED`) are still reported.
 */
export const isCancelledRequest = (error: unknown): boolean => {
  if (!error || typeof error !== 'object') return false;
  if (axios.isCancel(error)) return true;

  const maybe = error as {
    code?: string;
    name?: string;
    message?: string;
    __CANCEL__?: boolean;
    response?: unknown;
  };
  if (maybe.__CANCEL__) return true;
  if (maybe.code === 'ERR_CANCELED') return true;
  if (maybe.name === 'CanceledError' || maybe.name === 'AbortError') return true;
  // CancelToken / abort often surface only as a message with no response body.
  if (!maybe.response && typeof maybe.message === 'string' && CANCEL_MESSAGE_RE.test(maybe.message.trim())) {
    return true;
  }
  return false;
};

/**
 * True when this failure was (or will be) reported by axios error tracking / throwIfError.
 * Use in local catch blocks that wrap httpClient calls to avoid double-reporting.
 */
export const isAxiosErrorLike = (error: unknown): boolean => {
  if (!error || typeof error !== 'object') return false;
  const maybe = error as { isAxiosError?: boolean; config?: unknown; request?: unknown; response?: unknown };
  if (maybe.isAxiosError) return true;
  return maybe.config != null && (maybe.response !== undefined || maybe.request !== undefined);
};

/**
 * Read the business code out of an error response body. Pleased APIs use `code`, legacy
 * Sporty endpoints use `bizCode`; anything else (HTML error pages, blobs) yields nothing.
 */
export const getResponseBusinessCode = (data: unknown): number | string | undefined => {
  if (!data || typeof data !== 'object') return undefined;
  const { code, bizCode } = data as { code?: unknown; bizCode?: unknown };
  const candidate = code ?? bizCode;
  if (typeof candidate === 'number' || typeof candidate === 'string') return candidate;
  return undefined;
};
